import React, { useEffect, useState, Fragment, useContext } from 'react';
import {
  formatLocDateMMDDYY,
  formatPostDate,
} from '@mummssoftware/utils/formatters';
import { useIntl } from 'react-intl';
import { STRING_TRUE } from '@mummssoftware/utils/constants';
import { AppContext } from '../../../App/AppContext';
import { ApprovalHeaderTextContainer } from '../UnsignedApproval';
import defaults from '../../../../translations/en.json';
import { CertContext } from '../../../../Context';
import {
  MFST,
  PPN,
  PPNNO,
  REVIEW_OPEN,
  SET_REVIEW_TYPE,
  VST,
} from '../../../../constants';
import { UnreviewedItemBody } from './UnreviewedItemBody';
import { ReviewInput } from './ReviewInput';
import { ReviewContainerContext } from './ReviewContainerContext';
import { fetchPimreaderVisitDetails } from '../utils';

const reviewEntityCache = new Map();
const fetchDetailsWithCache = (
  agency,
  entityType,
  entityId,
  fetcher,
  retry = 0,
) => {
  const key = `${agency || 'default'}::${entityType}::${entityId}`;
  const hit = reviewEntityCache.get(key);
  if (hit) return Promise.resolve(hit);
  if (retry >= 2) return Promise.resolve(null);
  return fetcher().then(
    (payload) => {
      reviewEntityCache.set(key, payload);
      return payload;
    },
    () =>
      fetchDetailsWithCache(agency, entityType, entityId, fetcher, retry + 1),
  );
};

// Can't reuse noteHasPSO() from utils since some dtos don't have entityordertypeshortname filled in
const getPSOKey = (note) =>
  note.patientprogressnoteentitydto &&
  note.patientprogressnoteentitydto[0] &&
  note.patientprogressnoteentitydto[0].entitytypeshortname === 'PSO' &&
  note.patientprogressnoteentitydto[0].entitykey;

export const UnreviewedItem = (props) => {
  const {
    workflow,
    observation,
    approvalDTO,
    siteId,
    firstName,
    lastName,
    userCredentials,
    theme,
    hospiceBin,
    patientNumber,
    patientId,
  } = props;
  const { ApprovalHeaderText, ReviewedByCredentials } = defaults;
  const { userNumber, agency } = useContext(AppContext);
  const { keycloak } = useContext(CertContext);

  const { onSubmit } = useContext(ReviewContainerContext);

  const [entityDTO, setEntityDTO] = useState(null);
  const [pso, setPso] = useState(null);
  const [formattedHeader, setFormattedHeader] = useState(null);

  const { formatMessage } = useIntl();

  const reviewOpen = localStorage.getItem(SET_REVIEW_TYPE) === REVIEW_OPEN;
  const entityType = approvalDTO.entitytypeshortname;

  const checkReviewable = () =>
    reviewOpen && approvalDTO && approvalDTO.isreviewablebyuser === STRING_TRUE;

  const entityId = approvalDTO.entityid;

  // reset dto's when switching between approvals
  useEffect(() => {
    setEntityDTO(null);
    setPso(null);
    setFormattedHeader(makeReviewHeader());

    let getItemDetails;
    switch (entityType) {
      case PPN:
      case PPNNO:
        getItemDetails = () =>
          observation
            .getProgressNotesById(entityId, agency)
            .then(async (res) => {
              const psoKey = getPSOKey(res);
              return {
                entityDTO: res,
                pso: psoKey
                  ? await observation.getPatientStandingOrderByIdV2(
                      psoKey,
                      hospiceBin,
                    )
                  : null,
              };
            });
        break;
      case MFST:
        getItemDetails = () =>
          observation
            .getFajitaFormByIdByMode(entityId, null, agency)
            .then((res) => ({ entityDTO: res, pso: null }));
        break;
      case VST:
        getItemDetails = () =>
          fetchPimreaderVisitDetails(
            keycloak,
            agency,
            patientNumber,
            patientId,
            userNumber,
            entityId,
          ).then((res) => ({ entityDTO: res, pso: null }));
        break;
      default:
    }

    if (getItemDetails) {
      fetchDetailsWithCache(agency, entityType, entityId, getItemDetails).then(
        (payload) => {
          if (payload) {
            setEntityDTO(payload.entityDTO);
            setPso(payload.pso);
          }
        },
      );
    }
  }, [approvalDTO]);

  const makeReviewHeader = () => {
    const entityordertypeshortname = approvalDTO.entitytypename;
    const requestorpersonfirstname = approvalDTO.revieweepersonfirstname;
    const requestorpersonlastname = approvalDTO.revieweepersonlastname;
    const formattedVerbalDate = formatLocDateMMDDYY(
      approvalDTO.createdatetimeshifted,
    );
    return formatMessage(ApprovalHeaderText, {
      entityordertypeshortname,
      requestorpersonfirstname,
      requestorpersonlastname,
      formattedVerbalDate,
    });
  };

  const getReviewSubheader = () => (
    <div style={{ marginLeft: '15px' }}>
      {formatMessage(ReviewedByCredentials, {
        firstName: approvalDTO.reviewedbypersonfirstname,
        lastName: approvalDTO.reviewedbypersonlastname,
        userCredentials: approvalDTO.reviewedbypersoncredentials,
        reviewedDate: approvalDTO.reviewdatetime,
      })}
    </div>
  );

  const makeReviewBoilerplate = () => {
    const reviewedDate = formatLocDateMMDDYY(new Date());

    return formatMessage(ReviewedByCredentials, {
      firstName,
      lastName,
      userCredentials,
      reviewedDate,
    });
  };

  const updateApprovalDTO = (reviewText) => {
    let dto;
    if (approvalDTO) {
      dto = {
        ...approvalDTO,
        reviewedbypersonnumber: userNumber,
        reviewnote: reviewText,
        reviewdatetime: formatPostDate(new Date()),
        entityordertypeshortname: 'OTH', // TODO remove before production
        approvalstatusshortname: 'RW',
      };
    }
    return dto;
  };

  const handleSubmitNote = (notedto, reviewText) => {
    const oldNote = notedto.note;
    const dto = {
      ...notedto,
      // todo spelling error backend
      noteddatetime: notedto.noteddatetime || notedto.notedatetime,
      note: `${oldNote}\n\n **** ${reviewText} - ${makeReviewBoilerplate()} ****`,
    };
    return observation.saveProgressNote(dto, agency, 'PUT', 'true');
  };

  const handleSubmitManifest = (id, reviewText) => {
    const dto = {
      ishiddenordersandnotes: 'true',
      note: `**** ${reviewText}  - ${makeReviewBoilerplate()} ****`, // manifest renderer appends an attribution string automatically
      noteddatetime: formatPostDate(new Date()),
      patientnumber: approvalDTO.patientnumber,
      personnumber: userNumber,
      siteid: siteId,
      visitdatetime: null,
      patientprogressnoteentitydto: [
        {
          entitytypeshortname: 'MFST',
          entitykey: id,
          id: '',
        },
      ],
    };
    return observation.saveProgressNote(dto, agency, 'POST', 'false');
  };

  const handleSubmitVisit = (reviewText) => {
    const linkedForms = entityDTO.formsdto && JSON.parse(entityDTO.formsdto);
    const linkedNotes = entityDTO.notesdto && JSON.parse(entityDTO.notesdto);
    linkedForms &&
      linkedForms.forEach((form) => handleSubmitManifest(form.id, reviewText));
    linkedNotes &&
      linkedNotes.forEach((note) => handleSubmitNote(note, reviewText));

    const reviewNoteDTO = {
      note: `**** ${reviewText}  - ${makeReviewBoilerplate()} ****`,
      noteddatetime: formatPostDate(new Date()),
      patientnumber: patientNumber,
      personnumber: userNumber,
      patientprogressnoteentitydto: null,
      siteid: siteId.toString(),
      patientvisit_id: approvalDTO.entityid,
      ishiddenordersandnotes: true,
    };
    return observation.saveProgressNote(reviewNoteDTO, agency, 'POST', 'true');
  };

  const handleSubmit = (reviewText) => {
    const dto = updateApprovalDTO(reviewText);
    workflow.approveApproval(dto, agency).then(async () => {
      let res;
      switch (entityType) {
        case PPN:
        case PPNNO:
          res = handleSubmitNote(entityDTO, reviewText);
          break;
        case MFST:
          res = handleSubmitManifest(approvalDTO.entityid, reviewText);
          break;
        case VST:
          res = handleSubmitVisit(reviewText);
          break;
        default:
          break;
      }
      res.then(null, () => {
        workflow.approveApproval(approvalDTO, agency); // if observation update fails, reupload old approval object
      });
    });
    onSubmit();
  };

  return (
    <Fragment>
      <ApprovalHeaderTextContainer style={{ marginTop: '5vh' }}>
        {formattedHeader}
      </ApprovalHeaderTextContainer>
      {reviewOpen || getReviewSubheader()}

      <UnreviewedItemBody
        theme={theme}
        entityDTO={entityDTO}
        pso={pso}
        agency={agency}
        keycloak={keycloak}
        approvalDTO={approvalDTO}
      />
      <ReviewInput
        renderTextInput={checkReviewable()}
        onSubmit={handleSubmit}
      />
    </Fragment>
  );
};
